import { computed, ref, type ComputedRef, type Ref } from 'vue';
import type {
  AnnotationCoordinate,
  AnnotationFilter,
  AnnotationInstance,
  AnnotationScope,
  TemplateAnnotation,
  TemplateAnnotationWithPath,
  TemplateFieldType,
} from '@/api';

/**
 * Local-only id for tracking annotations in the UI before they round-trip
 * to the server. The backend doesn't issue annotation UIDs (annotations are
 * stored as a list keyed by coordinate), so we mint our own.
 */
function generateLocalId(): string {
  return `ann-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export interface AnnotationDraft extends TemplateAnnotation {
  /** Stable client-side id for v-for keys, focus-on-add, popover wiring, etc. */
  local_id: string;
  /**
   * The schema field this tag resolves to (`services[].fee` for a chip inside
   * a `{% for %}`), as served by the backend. Null for block tags and chips
   * that are not one field. Read-only: the server recomputes it on every
   * save, so a locally renamed tag keeps its old path until then.
   */
  schema_path?: string | null;
}

function ensureLocalId(annotation: TemplateAnnotationWithPath): AnnotationDraft {
  return { ...annotation, local_id: generateLocalId() };
}

export interface CaretResolution {
  scope: AnnotationScope;
  paragraph_index: number;
  run_index: number;
  offset_in_run: number;
  snippet_before: string;
  snippet_after: string;
  /** The exact run element the drop landed in — useful for visual feedback. */
  run_element: HTMLElement;
}

const SNIPPET_RADIUS = 24;

/**
 * Walk up from a node looking for an enclosing element with all the marker
 * attributes the backend renders into the preview HTML. Returns null if the
 * point isn't inside a tagged run (e.g. the user dropped on whitespace).
 */
function findRunElement(node: Node | null): HTMLElement | null {
  let current: Node | null = node;
  while (current) {
    if (
      current instanceof HTMLElement &&
      (current.dataset.runIndex !== undefined || current.dataset.rid !== undefined)
    ) {
      return current;
    }
    current = current.parentNode;
  }
  return null;
}

function readNumericDataset(el: HTMLElement, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = el.dataset[key];
    if (value !== undefined && value !== '') {
      const parsed = Number.parseInt(value, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return null;
}

function readScope(el: HTMLElement): AnnotationScope {
  const kind = (el.closest<HTMLElement>('[data-scope-kind]')?.dataset.scopeKind ?? 'body') as
    'body' | 'header' | 'footer';

  const indexValue = el.closest<HTMLElement>('[data-scope-index]')?.dataset.scopeIndex;
  const index = indexValue === undefined ? undefined : Number.parseInt(indexValue, 10);

  // `data-cell-path` is expected to be a JSON-serialised list of [t,r,c] triples.
  const cellPathValue = el.closest<HTMLElement>('[data-cell-path]')?.dataset.cellPath;
  let cell_path: [number, number, number][] | undefined;
  if (cellPathValue) {
    try {
      const parsed = JSON.parse(cellPathValue);
      if (Array.isArray(parsed)) {
        cell_path = parsed as [number, number, number][];
      }
    } catch {
      // ignore — invalid attribute, fall through with no cell path
    }
  }

  return { kind, ...(index === undefined ? {} : { index }), ...(cell_path ? { cell_path } : {}) };
}

/**
 * Resolve a (clientX, clientY) drop point to a docx coordinate inside the
 * given preview container. Returns null if the point isn't inside a tagged
 * run.
 */
export function resolveCaretToCoordinate(
  container: HTMLElement,
  clientX: number,
  clientY: number
): CaretResolution | null {
  // Standardise across browsers — Safari has caretRangeFromPoint, Firefox
  // has caretPositionFromPoint. We try both.
  let textNode: Node | null = null;
  let offsetInNode = 0;

  const docAny = document as Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  };

  if (typeof docAny.caretPositionFromPoint === 'function') {
    const pos = docAny.caretPositionFromPoint(clientX, clientY);
    if (pos) {
      textNode = pos.offsetNode;
      offsetInNode = pos.offset;
    }
  } else {
    /* eslint-disable @typescript-eslint/no-deprecated -- Safari WebKit fallback: caretRangeFromPoint is deprecated but it's the only caret-from-coordinates API in older WebKit builds */
    if (typeof docAny.caretRangeFromPoint === 'function') {
      const range = docAny.caretRangeFromPoint(clientX, clientY);
      if (range) {
        textNode = range.startContainer;
        offsetInNode = range.startOffset;
      }
    }
    /* eslint-enable @typescript-eslint/no-deprecated */
  }

  if (!textNode) return null;
  if (!container.contains(textNode)) return null;

  const runEl = findRunElement(textNode);
  if (!runEl) return null;

  const paragraph_index = readNumericDataset(runEl, 'paragraphIndex', 'pid');
  const run_index = readNumericDataset(runEl, 'runIndex', 'rid');
  if (paragraph_index === null || run_index === null) return null;

  // The dropped offset is within the text node Vue rendered; the run's full
  // text equals that node's textContent unless we've already injected a chip
  // into it. Build the snippets from the node's text so re-anchoring on a
  // re-uploaded source doc has something to fuzzy-match against.
  const runText = runEl.textContent;
  const safeOffset = Math.min(Math.max(offsetInNode, 0), runText.length);
  const snippet_before = runText.slice(Math.max(0, safeOffset - SNIPPET_RADIUS), safeOffset);
  const snippet_after = runText.slice(safeOffset, safeOffset + SNIPPET_RADIUS);

  return {
    scope: readScope(runEl),
    paragraph_index,
    run_index,
    offset_in_run: safeOffset,
    snippet_before,
    snippet_after,
    run_element: runEl,
  };
}

export function buildAnnotationCoordinate(resolution: CaretResolution): AnnotationCoordinate {
  return {
    scope: resolution.scope,
    paragraph_index: resolution.paragraph_index,
    run_index: resolution.run_index,
    offset_in_run: resolution.offset_in_run,
    snippet_before: resolution.snippet_before,
    snippet_after: resolution.snippet_after,
  };
}

export interface UseTemplateAnnotationsOptions {
  initial?: TemplateAnnotationWithPath[];
  /**
   * @deprecated `instances` is now derived from `annotations` and stays in
   * sync automatically. Pass `initial` and the derived view will reflect it.
   */
  initialInstances?: AnnotationInstance[];
}

/**
 * True for any annotation that was discovered by the backend in the source
 * `.docx` (vs. authored locally by the user). These are the only annotations
 * that can be "soft-deleted" — backend's re-discovery would otherwise bring
 * them back on the next save.
 */
export function isExtractedAnnotation(annotation: TemplateAnnotation): boolean {
  return annotation.origin === 'extracted' || annotation.origin_signature != null;
}

/**
 * Derive the entity/object instance roster from the current annotation list.
 * Mirrors the backend's aggregation logic so the alias dropdown in the
 * configure popover sees a brand-new role the moment it's added — no save
 * round-trip required.
 */
function deriveInstances(list: AnnotationDraft[]): AnnotationInstance[] {
  const map = new Map<string, AnnotationInstance>();
  for (const annotation of list) {
    if (annotation.deleted) continue;
    const binding = annotation.binding;
    if (!binding) continue;
    let key: string;
    let entry: AnnotationInstance;
    if (binding.kind === 'entity') {
      key = `entity:${binding.entity_type}:${binding.instance_alias}`;
      entry = map.get(key) ?? {
        kind: 'entity',
        entity_type: binding.entity_type,
        alias: binding.instance_alias,
        used_sub_fields: [],
      };
    } else if (binding.kind === 'object') {
      key = `object:${binding.instance_alias}`;
      entry = map.get(key) ?? {
        kind: 'object',
        entity_type: null,
        alias: binding.instance_alias,
        used_sub_fields: [],
      };
    } else {
      continue;
    }
    if (!map.has(key)) map.set(key, entry);
    const sub = (binding as { sub_field?: string }).sub_field;
    if (sub && !entry.used_sub_fields.includes(sub)) {
      entry.used_sub_fields.push(sub);
    }
  }
  return Array.from(map.values());
}

export function useTemplateAnnotations(options: UseTemplateAnnotationsOptions = {}): {
  annotations: Ref<AnnotationDraft[]>;
  instances: ComputedRef<AnnotationInstance[]>;
  serverSnapshot: Ref<TemplateAnnotation[]>;
  isDirty: Ref<boolean>;
  reset: (next: TemplateAnnotationWithPath[]) => void;
  add: (
    input: Omit<TemplateAnnotation, 'kind'> & { kind?: TemplateAnnotation['kind'] }
  ) => AnnotationDraft;
  update: (localId: string, patch: Partial<TemplateAnnotation>) => void;
  remove: (localId: string) => void;
  toPayload: () => TemplateAnnotation[];
  markSaved: (saved: TemplateAnnotationWithPath[]) => void;
} {
  const annotations = ref<AnnotationDraft[]>((options.initial ?? []).map(ensureLocalId));
  const serverSnapshot = ref<TemplateAnnotation[]>(snapshotFromList(annotations.value));

  /**
   * Live-derived from `annotations`. Stays in sync as the user adds, edits,
   * or removes tags — so the "existing roles" dropdown always reflects the
   * current state, not the last server snapshot.
   */
  const instances = computed<AnnotationInstance[]>(() => deriveInstances(annotations.value));

  const isDirty = computed(() => {
    const left = JSON.stringify(snapshotFromList(annotations.value));
    const right = JSON.stringify(serverSnapshot.value);
    return left !== right;
  });

  function reset(next: TemplateAnnotationWithPath[]): void {
    annotations.value = next.map(ensureLocalId);
    serverSnapshot.value = snapshotFromList(annotations.value);
  }

  function add(
    input: Omit<TemplateAnnotation, 'kind'> & { kind?: TemplateAnnotation['kind'] }
  ): AnnotationDraft {
    const draft: AnnotationDraft = {
      kind: input.kind ?? 'inline',
      block_id: input.block_id ?? null,
      anchor: input.anchor,
      field_name: input.field_name ?? null,
      binding: input.binding ?? null,
      filters: input.filters ?? [],
      condition: input.condition ?? null,
      required: input.required ?? false,
      origin: input.origin ?? 'user',
      origin_signature: input.origin_signature ?? null,
      local_id: generateLocalId(),
    };
    annotations.value = [...annotations.value, draft];
    return draft;
  }

  function update(localId: string, patch: Partial<TemplateAnnotation>): void {
    annotations.value = annotations.value.map(item =>
      item.local_id === localId ? { ...item, ...patch } : item
    );
  }

  /**
   * Remove an annotation.
   *
   * For extracted tags (those that came from parsing existing Jinja in the
   * source `.docx`) we keep them in the list with `deleted: true` so the
   * backend's materializer drops the original tag span on next save —
   * otherwise re-discovery would find the same `{{ ... }}` text still in
   * the source and bring the tag back. User-authored tags can just be
   * dropped from the list; they were never materialized to begin with.
   */
  function remove(localId: string): void {
    annotations.value = annotations.value.flatMap(item => {
      if (item.local_id !== localId) return [item];
      if (isExtractedAnnotation(item)) {
        return [{ ...item, deleted: true }];
      }
      return [];
    });
  }

  function toPayload(): TemplateAnnotation[] {
    return snapshotFromList(annotations.value);
  }

  function markSaved(saved: TemplateAnnotationWithPath[]): void {
    annotations.value = saved.map(ensureLocalId);
    serverSnapshot.value = snapshotFromList(annotations.value);
  }

  return {
    annotations,
    instances,
    serverSnapshot,
    isDirty,
    reset,
    add,
    update,
    remove,
    toPayload,
    markSaved,
  };
}

/** The wire shape: neither the client id nor the server-owned path is sent back. */
function snapshotFromList(list: AnnotationDraft[]): TemplateAnnotation[] {
  return list.map(({ local_id: _localId, schema_path: _schemaPath, ...rest }) => {
    void _localId;
    void _schemaPath;
    return rest;
  });
}

export type BlockKeyword = 'if' | 'elif' | 'else' | 'endif' | 'for' | 'endfor';

const BLOCK_KEYWORDS: readonly BlockKeyword[] = ['endif', 'endfor', 'elif', 'else', 'if', 'for'];

/** Strip surrounding `{{ }}` or `{% %}` from a stored field_name/condition. */
function stripJinjaWrappers(text: string): string {
  return text
    .replace(/^\s*\{\{\s*/, '')
    .replace(/\s*\}\}\s*$/, '')
    .replace(/^\s*\{%\s*/, '')
    .replace(/\s*%\}\s*$/, '')
    .trim();
}

export interface ParsedBlockTag {
  keyword: BlockKeyword;
  expression: string;
}

/**
 * Parse a block annotation's source text into its keyword + inner expression.
 * Handles backend payloads that store the whole tag (`"if is_vip_client"`,
 * `"{% for x in y %}"`, …) as well as bare expressions (`"is_vip_client"`).
 */
export function parseBlockTag(annotation: TemplateAnnotation): ParsedBlockTag {
  const fallbackKeyword: BlockKeyword =
    annotation.kind === 'block_end' ? 'endif' : annotation.kind === 'block_middle' ? 'else' : 'if';
  const candidates: string[] = [];
  if (annotation.field_name) candidates.push(annotation.field_name);
  if (annotation.condition) candidates.push(annotation.condition);

  for (const raw of candidates) {
    const stripped = stripJinjaWrappers(raw);
    for (const keyword of BLOCK_KEYWORDS) {
      const re = new RegExp(`^${keyword}\\b\\s*`, 'i');
      if (re.test(stripped)) {
        return { keyword, expression: stripped.replace(re, '').trim() };
      }
    }
  }

  // No keyword detected — assume the safe fallback for this slot and use
  // the bare condition / field_name as the expression.
  const expression = stripJinjaWrappers(annotation.condition ?? annotation.field_name ?? '');
  return { keyword: fallbackKeyword, expression };
}

/**
 * Split a `for` expression into its loop variable and iterable, so the UI
 * can edit each independently while keeping the literal `in` keyword fixed.
 * Matches the FIRST ` in ` so a complex iterable like `enumerate(items)`
 * stays intact on the right-hand side.
 */
export function splitForExpression(expression: string): { loopVar: string; iterable: string } {
  const match = expression.match(/^(.*?)\s+in\s+(.*)$/i);
  if (match) {
    return { loopVar: match[1].trim(), iterable: match[2].trim() };
  }
  return { loopVar: expression.trim(), iterable: '' };
}

/** Reassemble a `for` expression from its two parts, joined by ` in `. */
export function joinForExpression(loopVar: string, iterable: string): string {
  const left = loopVar.trim();
  const right = iterable.trim();
  if (!left && !right) return '';
  return `${left} in ${right}`;
}

/**
 * Build the canonical `field_name` string for a block annotation — the
 * format the materializer / render-tag endpoint expects:
 *   - `if is_vip_client`
 *   - `for service in services`
 *   - `endif`
 */
export function buildBlockFieldName(keyword: BlockKeyword, expression: string): string {
  const expr = expression.trim();
  if (!expr) return keyword;
  return `${keyword} ${expr}`;
}

/** Render a Jinja block tag as a string. */
function renderBlockTag(keyword: BlockKeyword, expression: string): string {
  const expr = expression.trim();
  const isPlainEnd = keyword === 'endif' || keyword === 'endfor' || keyword === 'else';
  if (isPlainEnd || !expr) return `{% ${keyword} %}`;
  return `{% ${keyword} ${expr} %}`;
}

/**
 * Format a default value the way the materializer does so the chip / live
 * preview reads like the actual emitted Jinja: strings are quoted, numbers
 * and booleans are passed through bare.
 */
function formatDefaultLiteral(value: string): string {
  if (value === 'true' || value === 'false') return value;
  if (value === 'null' || value === 'None') return value;
  if (/^-?\d+(?:\.\d+)?$/.test(value)) return value;
  return JSON.stringify(value);
}

/**
 * Best-effort offline Jinja preview — used for chip labels and as a
 * placeholder before the backend's `render-tag` round-trip resolves.
 * The backend's response is the canonical source of truth.
 */
export function renderJinjaPreview(annotation: TemplateAnnotation): string {
  if (
    annotation.kind === 'block_start' ||
    annotation.kind === 'block_middle' ||
    annotation.kind === 'block_end'
  ) {
    const { keyword, expression } = parseBlockTag(annotation);
    return renderBlockTag(keyword, expression);
  }
  const defaultPart = annotation.default_value
    ? ` | default(${formatDefaultLiteral(annotation.default_value)})`
    : '';
  // Selection fields materialize as ` | choices(...)` on the backend. The
  // chip preview has to include that filter character-for-character —
  // otherwise the chip lookup in `insertChipsForRun` won't match the
  // original `{{ x | choices(...) }}` text and the chip gets inserted
  // alongside the source instead of over it.
  const choicesPart = annotation.selection_options?.length
    ? ` | choices(${annotation.selection_options.map(opt => JSON.stringify(opt)).join(', ')})`
    : '';
  const filters = (annotation.filters ?? []).map(filterToString).join('');
  return `{{ ${variableNameForPreview(annotation)}${choicesPart}${defaultPart}${filters} }}`;
}

/**
 * The Jinja variable a tag renders — `natural_person_seller.name`,
 * `client.reference`, `effective_date`. Outside a loop this is also the
 * schema path; inside one the loop variable stands in for the list.
 */
export function variableNameForPreview(annotation: TemplateAnnotation): string {
  const binding = annotation.binding ?? null;
  if (binding) {
    if (binding.kind === 'entity') {
      return `${binding.entity_type}_${binding.instance_alias}.${binding.sub_field}`;
    }
    if (binding.kind === 'object') {
      return `${binding.instance_alias}.${binding.sub_field}`;
    }
  }
  const name = annotation.field_name ?? 'field';
  // Defensive: if backend handed back a field_name still wrapped in braces
  // (e.g. tag-discoverer kept "{{ loop.index }}" verbatim), unwrap it so the
  // chip doesn't render as "{{ {{ loop.index }} }}".
  return stripJinjaWrappers(name) || name;
}

/**
 * Backend stores filter args as raw string values without quotes
 * (`["en", "long"]`). When rendering back to Jinja source we have to
 * re-quote string-typed arguments so `format_date("en", "long")` round-trips
 * correctly — otherwise the docx ends up with `format_date(en, long)` which
 * jinja will try to resolve as identifiers and fail at render time.
 */
function quoteArgIfNeeded(arg: string): string {
  if (arg.length >= 2) {
    const first = arg[0];
    const last = arg.at(-1);
    if ((first === '"' && last === '"') || (first === "'" && last === "'")) {
      return arg;
    }
  }
  if (/^-?\d+(\.\d+)?$/.test(arg)) return arg;
  if (arg === 'true' || arg === 'false' || arg === 'null' || arg === 'None') return arg;
  const escaped = arg.replace(/\\/g, '\\\\').replace(/"/g, String.raw`\"`);
  return `"${escaped}"`;
}

function filterToString(filter: AnnotationFilter): string {
  const args = (filter.args ?? []).map(quoteArgIfNeeded).join(', ');
  return args.length > 0 ? ` | ${filter.name}(${args})` : ` | ${filter.name}`;
}

/** Helper to filter a catalog by which type of field is being inserted. */
export function filtersForType<T extends { applicable_types: TemplateFieldType[] }>(
  filters: T[],
  type: TemplateFieldType | null | undefined
): T[] {
  if (!type) return filters;
  return filters.filter(filter => filter.applicable_types.includes(type));
}

/**
 * "Format" filters (`format_currency`, `format_date`, `format_number`,
 * `format_ordinal`, `format_cardinal`) are the locale/formatting family — the
 * catalog groups them under category `custom`. Each produces a final
 * presentation string and they don't compose, so a tag carries at most one.
 */
export function isFormatFilterDef(
  def: { name: string; category: string } | null | undefined
): boolean {
  return def != null && (def.category === 'custom' || def.name.startsWith('format_'));
}

interface FilterLike {
  name: string;
  args?: string[];
}

/**
 * Put `incoming` filters onto another tag of the same field without breaking
 * it. For each one: a filter of the same name already there takes the new
 * arguments in place; a format filter replaces the format filter already
 * there (never two — `format_number` + `format_currency` fails at render);
 * otherwise it is added — a format filter first, so it sees the raw value,
 * anything else at the end. `replaced` names the format filters that gave
 * way, for the author to be told.
 */
export function mergeFiltersInto(
  existing: FilterLike[],
  incoming: FilterLike[],
  catalog: { name: string; category: string }[]
): { filters: FilterLike[]; replaced: string[] } {
  const isFormat = (name: string): boolean =>
    isFormatFilterDef(catalog.find(item => item.name === name));
  const filters: FilterLike[] = existing.map(item => ({
    name: item.name,
    args: [...(item.args ?? [])],
  }));
  const replaced: string[] = [];
  for (const filter of incoming) {
    const copy: FilterLike = { name: filter.name, args: [...(filter.args ?? [])] };
    const sameName = filters.findIndex(item => item.name === filter.name);
    if (sameName !== -1) {
      filters[sameName] = copy;
      continue;
    }
    if (isFormat(filter.name)) {
      const otherFormat = filters.findIndex(item => isFormat(item.name));
      if (otherFormat === -1) {
        filters.unshift(copy);
      } else {
        replaced.push(filters[otherFormat].name);
        filters[otherFormat] = copy;
      }
      continue;
    }
    filters.push(copy);
  }
  return { filters, replaced };
}
